import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ShoppingBag, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const STATUS_STYLES = {
  synced: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40',
  sync_failed: 'bg-red-500/20 text-red-400 border-red-500/40',
  not_eligible: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/40',
};

const SOURCE_LABELS = {
  mamopay_webhook: 'MamoPay link', mamopay_external_verify: 'MamoPay (verified)',
  mamopay_manual_mark: 'MamoPay (manual)', stripe_webhook: 'Stripe link',
  stripe_verify_session: 'Stripe link', settlement: 'Cash settlement',
  manual_retry: 'Manual retry', manager_receipt: 'Manager receipt',
};

export default function ShopifyPaidSyncLog({ formatCurrency }) {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/payments/shopify-sync-log?limit=150`, auth());
      setEntries(resp.data.entries || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load sync log'); }
    finally { setLoading(false); }
  };

  const openDialog = () => { setOpen(true); load(); };

  const retry = async (entry) => {
    setRetrying(entry.id);
    try {
      const resp = await axios.post(`${API}/payments/shopify-sync-retry`, { order_id: entry.order_id }, auth());
      const st = resp.data.result?.status;
      toast[st === 'synced' ? 'success' : 'error'](`Retry: ${st}${resp.data.result?.error ? ` — ${resp.data.result.error}` : ''}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Retry failed'); }
    finally { setRetrying(null); }
  };

  return (
    <>
      <Button variant="outline" size="sm" className="border-wt-border" onClick={openDialog} data-testid="shopify-sync-log-btn">
        <ShoppingBag className="w-4 h-4 mr-1" />Shopify Sync Log
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-4xl">
          <DialogHeader><DialogTitle className="text-wt-text-primary">Shopify Paid Sync Log</DialogTitle></DialogHeader>
          <p className="text-xs text-wt-text-muted">
            Every order Opswarex auto-marked (or tried to mark) as paid on Shopify — payment links, manager cash receipts and COD settlements.
          </p>
          <div className="max-h-[60vh] overflow-auto" data-testid="shopify-sync-log-table">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-wt-text-muted border-b border-wt-border/50 text-left">
                  <th className="py-2 pr-2">When</th><th className="pr-2">Order</th>
                  <th className="pr-2 text-right">Amount</th><th className="pr-2">Via</th>
                  <th className="pr-2">Status</th><th className="pr-2">Detail</th><th></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id} className="border-b border-wt-border/30 text-wt-text-primary" data-testid={`sync-log-row-${e.id}`}>
                    <td className="py-2 pr-2 whitespace-nowrap text-wt-text-muted">{(e.at || '').slice(0, 16).replace('T', ' ')}</td>
                    <td className="pr-2 font-medium">{e.order_number || e.order_id}</td>
                    <td className="pr-2 text-right">{formatCurrency ? formatCurrency(e.amount) : e.amount}</td>
                    <td className="pr-2 whitespace-nowrap">{SOURCE_LABELS[e.source] || e.source}<span className="text-wt-text-muted"> · {e.gateway}</span></td>
                    <td className="pr-2"><Badge className={`text-[10px] ${STATUS_STYLES[e.status] || STATUS_STYLES.not_eligible}`}>{(e.status || '').replace('_', ' ')}</Badge></td>
                    <td className="pr-2 max-w-[220px] truncate text-wt-text-muted" title={e.error || ''}>{e.error || (e.transaction_id ? `txn ${e.transaction_id}` : '—')}</td>
                    <td>
                      {e.status === 'sync_failed' && (
                        <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[10px]" disabled={retrying === e.id}
                          onClick={() => retry(e)} data-testid={`sync-retry-${e.id}`}>
                          <RefreshCw className="w-3 h-3 mr-1" />{retrying === e.id ? '…' : 'Retry'}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!loading && entries.length === 0 && (
              <p className="text-center text-wt-text-muted text-sm py-6">No Shopify paid-sync activity yet</p>
            )}
            {loading && <p className="text-center text-wt-text-muted text-sm py-6">Loading…</p>}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
